import { useEffect, useState, useRef, type ReactNode, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from '../Button';
import { Checkbox } from '../Checkbox';

export interface ModalFormProps {
  /**
   * Si el modal está abierto
   */
  isOpen: boolean;
  
  /**
   * Título del modal
   */
  title: string;
  
  /**
   * Callback al cerrar
   */
  onClose: () => void;
  
  /**
   * Callback al enviar el formulario
   */
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  
  /**
   * Contenido del formulario (campos)
   */
  children: ReactNode;
  
  /**
   * Si está enviando (loading state)
   */
  isSubmitting?: boolean;
  
  /**
   * Texto del botón de submit
   * @default "Guardar"
   */
  submitLabel?: string;
  
  /**
   * Texto del botón de cancelar
   * @default "Cancelar"
   */
  cancelLabel?: string;
  
  /**
   * Tamaño del modal
   * @default "medium"
   */
  size?: 'small' | 'medium' | 'large';
  
  /**
   * Si se debe cerrar al hacer click fuera
   * @default true
   */
  closeOnClickOutside?: boolean;
  
  /**
   * Si se debe cerrar al presionar ESC
   * @default true
   */
  closeOnEsc?: boolean;
  
  /**
   * Clase CSS adicional para el contenido
   */
  className?: string;
  
  /**
   * Si se muestra el checkbox "Crear otro"
   * @default false
   */
  showCreateAnother?: boolean;
  
  /**
   * Texto del checkbox "Crear otro"
   * @default "Crear otro"
   */
  createAnotherLabel?: string;
  
  /**
   * Callback cuando cambia el estado de "Crear otro"
   */
  onCreateAnotherChange?: (value: boolean) => void;
  
  /**
   * Acciones personalizadas para mostrar en el header (antes del botón cerrar)
   */
  headerActions?: ReactNode;
  
  /**
   * Si el formulario es válido y puede ser enviado
   * @default true
   */
  canSubmit?: boolean;
  
  /**
   * Contenido a mostrar encima del footer (ej: indicadores de estado)
   */
  footerTop?: ReactNode;
  /**
   * Oculta el botón de submit (modo solo lectura)
   */
  hideSubmit?: boolean;
  /**
   * Clase CSS adicional para el botón de submit (ej: variante danger)
   */
  submitClassName?: string;
}

/**
 * Modal de Formulario Reutilizable
 * 
 * Componente completo para modales con formularios. Incluye overlay, header, footer,
 * gestión de estados y validaciones automáticas.
 * 
 * @example
 * ```tsx
 * <ModalForm
 *   isOpen={isOpen}
 *   title="Nueva Empresa"
 *   onClose={() => setIsOpen(false)}
 *   onSubmit={handleSubmit}
 *   isSubmitting={esCargando}
 * >
 *   <Input label="RUC" {...rucProps} />
 *   <Input label="Razón Social" {...razonProps} />
 * </ModalForm>
 * ```
 */
export const ModalForm: React.FC<ModalFormProps> = ({
  isOpen,
  title,
  onClose,
  onSubmit,
  children,
  isSubmitting = false,
  submitLabel = 'Guardar',
  cancelLabel = 'Cancelar',
  size = 'medium',
  closeOnClickOutside = true,
  closeOnEsc = true,
  className = '',
  showCreateAnother = false,
  createAnotherLabel = 'Crear otro',
  onCreateAnotherChange,
  headerActions,
  canSubmit = true,
  footerTop,
  hideSubmit = false,
  submitClassName = '',
}) => {
  const [createAnother, setCreateAnother] = useState(false);
  const mouseDownOnBackdrop = useRef(false);

  // Cerrar con ESC
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, closeOnEsc, isSubmitting, onClose]);
  
  // Prevenir scroll del body cuando está abierto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  if (!isOpen) return null;

  // Tamaños del modal
  const sizeClasses = {
    small: 'max-w-md',
    medium: 'max-w-2xl',
    large: 'max-w-4xl'
  };
  
  const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    mouseDownOnBackdrop.current = e.target === e.currentTarget;
  };

  const handleBackdropMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (mouseDownOnBackdrop.current && e.target === e.currentTarget && closeOnClickOutside && !isSubmitting) {
      onClose();
    }
    mouseDownOnBackdrop.current = false;
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div
        className={`w-full ${sizeClasses[size]} bg-surface-1 rounded-2xl shadow-2xl flex flex-col max-h-[90vh] animate-in fade-in zoom-in-95 duration-200`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-2.5 border-b border-default flex-shrink-0">
          <h2 className="font-display text-h4 text-primary">
            {title}
          </h2>
          <div className="flex items-center gap-2">
            {headerActions && <div className="flex items-center">{headerActions}</div>}
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg p-2 text-tertiary transition-colors hover:bg-surface-hover hover:text-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        
        {/* Form */}
        <form onSubmit={onSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Content */}
          <div className={`px-6 py-4 space-y-6 overflow-y-auto flex-1 ${className}`}>
            {children}
          </div>
          
          {/* Footer Top (optional - e.g. status indicators) */}
          {footerTop && (
            <div className="px-6 py-2 border-t border-default flex-shrink-0">
              {footerTop}
            </div>
          )}
          
          {/* Footer */}
          <div className={`flex items-center justify-between px-6 py-4 ${footerTop ? '' : 'border-t'} border-default flex-shrink-0`}>
            {/* Checkbox "Crear otro" */}
            {showCreateAnother && (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  checked={createAnother}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setCreateAnother(newValue);
                    onCreateAnotherChange?.(newValue);
                  }}
                  disabled={isSubmitting}
                />
                <span className="text-ui-sm text-secondary">{createAnotherLabel}</span>
              </label>
            )}
            
            {/* Botones */}
            <div className={`flex gap-3 ${showCreateAnother ? '' : 'ml-auto'}`}>
              <Button
                type="button"
                onClick={onClose}
                variant="secondary"
                disabled={isSubmitting}
              >
                {cancelLabel}
              </Button>
              {!hideSubmit && (
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting || !canSubmit}
                  className={submitClassName}
                >
                  {isSubmitting ? 'Guardando...' : submitLabel}
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

ModalForm.displayName = 'ModalForm';

export default ModalForm;


